import axios from 'axios'
import { tokenStorage } from '@/lib/auth/tokenStorage'

const API_BASE_URL = import.meta.env.VITE_API_URL as string | undefined ?? 'http://localhost:8000/api'

export const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
})

// ─── Request interceptor: attach JWT + strip undefined params ─────────────────

axiosClient.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  // Remove undefined/null/empty-string params to avoid ?key=undefined in URLs
  if (config.params) {
    const cleaned: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(config.params as Record<string, unknown>)) {
      if (value !== undefined && value !== null && value !== '') {
        cleaned[key] = value
      }
    }
    config.params = cleaned
  }

  return config
})

// ─── Response interceptor: auto-refresh on 401 ───────────────────────────────

let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token)
    } else {
      prom.reject(error)
    }
  })
  failedQueue = []
}

function forceLogout() {
  tokenStorage.clearTokens()
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

axiosClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.config) {
      return Promise.reject(error)
    }

    // Network error (server down) — don't attempt auth refresh, just reject
    if (!error.response) {
      return Promise.reject(error)
    }

    const originalRequest = error.config as typeof error.config & { _retry?: boolean }
    const url = originalRequest.url ?? ''

    // Never retry auth endpoints to prevent infinite loops
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh')

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return axiosClient(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refresh = tokenStorage.getRefreshToken()
      if (!refresh) {
        isRefreshing = false
        forceLogout()
        return Promise.reject(error)
      }

      try {
        const { data } = await axios.post<{ access: string }>(
          `${API_BASE_URL}/auth/refresh/`,
          { refresh },
        )
        tokenStorage.setAccessToken(data.access)
        processQueue(null, data.access)
        originalRequest.headers.Authorization = `Bearer ${data.access}`
        return axiosClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        forceLogout()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // If 401 on auth endpoints (login failed, etc.), don't redirect — just reject
    return Promise.reject(error)
  },
)
